import type { Cbt } from './cbt';
import type { GpuCbtTileState } from './gpu-cbt';

// --- Manifest (matches serving/global_manifest.json) ---

export interface GlobalManifest {
  slots: number;
  lod0_res: number;
  hires_res: number;
  n_lat: number;
  n_lon: number;
  tiles_dir: string;
  tiles: GlobalTileInfo[];
}

export interface GlobalTileInfo {
  slot: number;
  tile_id: number;
  trained: boolean;
  lat_min: number;
  lat_max: number;
  lon_min: number;
  lon_max: number;
  elev_min: number;
  elev_max: number;
}

// --- Tile slot (static info) ---

export interface TileSlot {
  tileId: number;
  trained: boolean;
  elevMin: number;
  elevMax: number;
  row: number;
  col: number;
  offsetX: number;
  offsetY: number;
  offsetZ: number;
}

// --- Per-tile GPU render state ---

export interface TileRenderState {
  bindGroup: GPUBindGroup;
  uniformBuffer: GPUBuffer;
  heightmapTexture: GPUTexture;
  heightmapView: GPUTextureView;
  vertexBuffer: GPUBuffer;
  indexBuffer: GPUBuffer;
  numIndices: number;
  vertexCapacity: number;
  indexCapacity: number;
  cbt: Cbt;
  currentLod: LodLevel;
  lastVisibleFrame: number;
  upgradeFrame: number;
  elevMin: number;
  elevMax: number;
  texelSize: number;

  // Inference fields (undefined for LOD0-only tiles)
  model?: TileModelBuffers;
  hiresTexture?: GPUTexture;
  occupancy: Uint32Array;
  refinementCount: number;

  // Detail patch (2-level clipmap)
  detailTexture?: GPUTexture;
  detailView?: GPUTextureView;
  detailOriginU: number;
  detailOriginV: number;
  detailScale: number;
  detailOccupancy?: Uint32Array;
  detailRefinementCount: number;
  detailModel?: TileModelBuffers;

  // GPU CBT state (undefined when using CPU CBT path)
  gpuCbt?: GpuCbtTileState;

  // Per-tile mesh cache — avoids per-frame world-space copy
  _lastVertices?: Float32Array;
  _lastIndices?: Uint32Array;
  _worldVerts?: Float32Array;
}

export const enum LodLevel {
  Lod0 = 0,
  Lod1 = 1,
}

// --- Per-tile inference model resources ---

export interface TileModelBuffers {
  weightsBuf: GPUBuffer;
  freqsBuf: GPUBuffer;
  gridTex: GPUTexture;
  gridView: GPUTextureView;
  gridSampler: GPUSampler;
  paramsBuf: GPUBuffer;
  coordsBuf: GPUBuffer;
  outBuf: GPUBuffer;
  readbackBufs: [GPUBuffer, GPUBuffer]; // double-buffered
  readbackFront: number; // 0 or 1
  /** True when the readback buffer has been mapped and is ready to read. */
  readbackReady: boolean;
  /** Coords from the dispatch whose readback is now ready (or pending). */
  pendingCoords: Uint32Array | null;
  /** If true, pending results should be consumed (to unmap buffer) but not scattered. */
  pendingDiscard: boolean;
  bindGroup: GPUBindGroup;
  shadowData: Uint8Array;
  dirtyRows: BigUint64Array;
  outputRes: number;
  baseParams: InferParams;
}

export interface InferParams {
  outputRes: number;
  nCoords: number;
  gridMin: number;
  gridScale: number;
  elevMin: number;
  elevRange: number;
  residualScale: number;
  coordScale: number;       // 1.0 for full tile, DETAIL_SCALE for detail patch
  coordOffsetU: number;     // 0.0 for full tile, detail origin U for detail patch
  coordOffsetV: number;     // 0.0 for full tile, detail origin V for detail patch
}

// --- Parsed weight file ---

export interface TileWeights {
  tileId: number;
  gridRes: number;
  gridMin: number;
  gridMax: number;
  gridScale: number;
  elevMin: number;
  elevMax: number;
  gridData: Uint8Array;
  freqs: Float32Array;
  mlpWeights: Float32Array;
}

export const DETAIL_RES = 1024;
export let DETAIL_SCALE = 0.25;  // 1/4 tile per axis = 4× resolution

// Effective resolution options: 1024 (no detail zoom), 2048, 4096, 8192
export const DETAIL_ZOOM_OPTIONS = [1, 2, 4, 8, 16] as const;

export const HIRES_RES_OPTIONS = [1024, 2048, 4096] as const;
export let HIRES_RES = 1024;
export let MAX_LOD1_TILES = 20;
export const INFER_BUDGET = 4096;
export const DOWNGRADE_FRAMES = 300;
export const UPGRADE_HOLD_FRAMES = 600;

// ~200MB GPU memory budget for LOD1 tiles (base + detail per tile)
const LOD1_MEMORY_BUDGET = 200 * 1024 * 1024;
// Per tile: base heightmap (2MB) + detail heightmap (2MB) + base shadow (2MB) + detail shadow (2MB) + occupancy
const LOD1_BYTES_PER_TILE = () => HIRES_RES * HIRES_RES * 2 * 2 + DETAIL_RES * DETAIL_RES * 2 * 2 + (DETAIL_RES * DETAIL_RES / 8);

export function setDetailZoom(zoom: number): void {
  DETAIL_SCALE = 1.0 / zoom;
}

export function setHiresRes(res: number): void {
  HIRES_RES = res;
  MAX_LOD1_TILES = Math.max(2, Math.min(20, (LOD1_MEMORY_BUDGET / LOD1_BYTES_PER_TILE()) | 0));
}
